import Vue from 'vue';
import { customerExposer } from '@pirates/not-morty-biz';
import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { DEFAULT_COUNTRY_CODE } from '@/data/country.config';
import { t, TRANSLATION_CONST } from '@/utils/translation';
import i18n, { defaultLocale } from '@/localization/i18n';
import { sentry } from '@/utils/sentry';
import { getSupportedLocale } from '@/localization';
import { localStorage } from '@/utils/local-storage';
import { loadUserCentrics, initUsercentricsConsentListeners } from '@/utils/usercentrics';
import { loadHotJar } from '@/utils/load-hot-jar';
import { loadTranslations } from '@/localization/translations';

interface ErrorWithResponse extends Error {
  response?: any;
}

export interface AppState {
  appReady: boolean;
  logoClass: string;
  homeURL: string;
  activeLanguage: string;
  activeCountry: string;
  businessModeActive: boolean;
  contentLoaded: boolean;
  isWebView: boolean;
  locations: Array<any>;
  navOpen: boolean;
  isHeaderHidden: boolean;
  isHeaderMenuHidden: boolean;
  isFooterHidden: boolean;
}

const parseLocaleString: (countryLang: string) => { country: string; language: string } = (countryLang: string) => {
  const matches: string[] | null = countryLang.match(/([a-z]+)-([A-Z]+)/);
  if (matches !== null) {
    return {
      country: matches[2],
      language: matches[1],
    };
  }

  return defaultLocale;
};

const setLocale = (country: string, language: string, isFallback?: boolean) => {
  const locale = getSupportedLocale(`${language.toLowerCase()}-${country.toUpperCase()}`);
  const translations = loadTranslations(locale);
  if (!isFallback) {
    i18n.locale = `${language}-${country}`;
  }

  i18n.setLocaleMessage(i18n.locale, translations);
  i18n.fallbackLocale = `${defaultLocale.language}-${country}`;

  return translations;
};

const getLocale = () => {
  const { language } = navigator;
  if (language.indexOf('-') > -1) {
    return getSupportedLocale(language);
  } else {
    return getSupportedLocale(`${language}-${language === 'en' ? defaultLocale.country : language.toUpperCase()}`);
  }
};

class App implements Module<any, any> {
  public state: AppState = {
    appReady: false,
    logoClass: 'logo',
    homeURL: '/business/',
    activeLanguage: defaultLocale.language,
    activeCountry: DEFAULT_COUNTRY_CODE,
    businessModeActive: true,
    contentLoaded: false,
    isWebView: true,
    locations: [],
    navOpen: false,
    isHeaderHidden: false,
    isHeaderMenuHidden: false,
    isFooterHidden: false,
  };
  public namespaced: boolean = true;

  public actions = {
    toggleWebView(store: ActionContext<any, any>, isWebView: boolean) {
      store.commit('TOGGLE_WEBVIEW', isWebView);
    },
    toggleMenu(store: ActionContext<any, any>) {
      store.commit('TOGGLE_MENU');
    },
    showError(
      _: ActionContext<any, any>,
      payload: {
        title: string;
        description: string;
        onClose?: () => void;
        err?: Error;
      },
    ) {
      const { title, description, onClose, err } = payload;

      // log out error to console during development
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error(err);
        return;
      }

      const errorUuid = Vue.utils.$notify(title, description, onClose);

      sentry.withScope(function (scope) {
        scope.setTags({ errorUuid });

        if (err) {
          sentry.captureException(err);
        } else {
          sentry.captureMessage('Something went wrong');
        }
      });
    },
    showServerError(store: ActionContext<any, any>, err?: ErrorWithResponse) {
      const responseData = err && err.response && err.response.data;
      const errorCode = responseData && responseData.errorCode && responseData.errorCode;

      let errorTitle;
      let errorDescription;

      switch (errorCode) {
        case 'DUPLICATED_DRIVER':
          errorTitle = 'error_duplicated_driver';
          errorDescription = 'error_duplicated_driver_description';
          break;
        default:
          errorTitle = 'error_msg_server';
          errorDescription = 'error_msg_server_description';
      }

      store.dispatch('showError', {
        title: t(errorTitle),
        description: t(errorDescription),
        err,
      });
    },
    setCustomerContext(store: ActionContext<any, any>) {
      store.commit('SET_CUSTOMER_CONTEXT');
    },
    setBusinessContext(store: ActionContext<any, any>) {
      store.commit('SET_BUSINESS_CONTEXT');
    },
    toggleContentLoaded(store: ActionContext<any, any>, isLoaded: boolean) {
      store.commit('TOGGLE_CONTENT_LOADED', isLoaded);
    },
    setAppReady(store: ActionContext<any, any>, ready: boolean) {
      store.commit('SET_APP_READY', ready);
    },
    async loadLocaleFromUrl(store: ActionContext<any, any>, params) {
      const isValidLocaleString = params && params.locale;
      const localeString = isValidLocaleString ? params.locale : localStorage.get(TRANSLATION_CONST) || getLocale() || `${defaultLocale.language}-${defaultLocale.country}`;

      // Pass country code to loadUserCentrics method
      const countryCode = localeString.split('-')[1];
      loadUserCentrics();
      loadHotJar(countryCode);
      initUsercentricsConsentListeners();

      await store.dispatch('loadTranslations', {
        isTemp: true,
        localeString,
      });
    },
    async loadTranslations(store: ActionContext<any, any>, { isTemp, localeString }) {
      localeString = localeString.replace('_', '-');
      const localeObject = parseLocaleString(localeString);

      let country = localeObject.country;
      let activeLanguage = localeObject.language;
      let translations = setLocale(country, activeLanguage);
      // Default to British English when translations are missing
      if (Object.keys(translations).length === 0) {
        country = defaultLocale.country;
        activeLanguage = defaultLocale.language;
        translations = setLocale(country, activeLanguage, true);
      }

      store.commit('LOAD_TRANSLATIONS', {
        activeLanguage,
        translations,
        activeCountry: country === 'GB' ? DEFAULT_COUNTRY_CODE : country,
      });

      const { UC_UI } = window as any;
      // Update User centrics UI language
      if (UC_UI) {
        UC_UI.updateLanguage(activeLanguage);
      }

      await store.dispatch(
        'users/updateUserLanguage',
        {
          isTemp,
          localeString,
        },
        { root: true },
      );
    },
  } as ActionTree<any, any>;

  public getters = {
    isTranslationsReady(state: any): boolean {
      return !!state.translations;
    },

    activeLocale(state: any): string {
      return `${state.activeLanguage}-${state.activeCountry}`;
    },
  } as GetterTree<any, any>;

  public mutations = {
    TOGGLE_WEBVIEW(state: any, isWebView: boolean) {
      state.isWebView = isWebView;
    },
    TOGGLE_MENU(state: any) {
      state.navOpen = !state.navOpen;
    },
    async SET_CUSTOMER_CONTEXT() {
      await customerExposer.setCustomerContext('own');
    },
    async SET_BUSINESS_CONTEXT() {
      await customerExposer.setCustomerContext('company');
    },
    TOGGLE_CONTENT_LOADED(state: any, isLoaded: boolean) {
      state.contentLoaded = isLoaded;
    },
    SET_LOCATIONS(state: any, locations: any[]) {
      state.locations = locations;
    },
    LOAD_TRANSLATIONS(state: any, translationData: { translations: object; activeLanguage: string; activeCountry: string }) {
      Vue.set(state, 'translations', translationData.translations);
      Vue.set(state, 'activeLanguage', translationData.activeLanguage);
      Vue.set(state, 'activeCountry', translationData.activeCountry);
    },
    SET_APP_READY(state: any, ready: boolean) {
      state.appReady = ready;
    },
    // Use logoClass of 'brand-logo' to use co-branding logo;
    SET_LOGO_CLASS(state: any, logoClass: string) {
      state.logoClass = logoClass;
    },
    SET_HOME_URL(state: any, homeURL: string) {
      state.homeURL = homeURL;
    },
    TOGGLE_HEADER_INVISIBILITY(state: any, visibility: boolean) {
      state.isHeaderHidden = visibility;
    },
    TOGGLE_HEADER_MENU_INVISIBILITY(state: any, visibility: boolean) {
      state.isHeaderMenuHidden = visibility;
    },
    TOGGLE_FOOTER_INVISIBILITY(state: any, visibility: boolean) {
      state.isFooterHidden = visibility;
    },
  } as MutationTree<any>;
}

export default new App();
